import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { isTL, load } from '@store/orders';

const RESTRICTED_COMMODITY_IDS = new Set<number>([
  6, // Clothing
  8, // Electronics/Appliances
  9, // Non Perishable Food Products- No Temp Control
  10, // Perishable Foodstuff - Temp Control
  17, // Metals
  20, // Personal Care
  32, // Foodstuff - Temp Controlled
  45, // Pet Products
  47, // Perishable Foodstuff - No Temp Control
]);

const TERMS_ACCEPTED = 'terms_accepted';

const ELIGIBILITY_REJECTION_CODE = 'GetLoadSureRatesCommandHandler';

const CARRIER_RULE_PREFIX = 'carrier ';

const SECURE_RATE_CON_RULE_PREFIX = 'the secure ratecon';

const isRestrictedCommodity = (commodityId: number | null) =>
  commodityId !== null && RESTRICTED_COMMODITY_IDS.has(commodityId);

const secureRateConForCarrier = () => {
  const currentLoad = load();
  const carrierId = currentLoad?.carrierId;

  if (carrierId === null || carrierId === undefined) {
    return null;
  }

  return (
    currentLoad?.secureRateConViewModels?.find(
      (rateCon) => rateCon.carrierId === carrierId,
    ) ?? null
  );
};

const hasAcceptedSecureRateCon = () => {
  const rateCon = secureRateConForCarrier();

  if (!rateCon) {
    return false;
  }

  if (rateCon.status.trim().toLowerCase() === TERMS_ACCEPTED) {
    return true;
  }

  return Boolean(
    rateCon.secureRateConHistory?.some(
      (history) => history.actionType.trim().toLowerCase() === TERMS_ACCEPTED,
    ),
  );
};

export type LoadSureRejection = {
  carrierFailures: string[];
  secureRateConFailure: string | null;
};

export const getLoadSureEligibilityRejection = (
  error: unknown,
): LoadSureRejection => {
  const rejection: LoadSureRejection = {
    carrierFailures: [],
    secureRateConFailure: null,
  };

  if (!Array.isArray(error)) {
    return rejection;
  }

  error.forEach((entry: unknown) => {
    if (
      typeof entry !== 'object' ||
      entry === null ||
      !('propertyName' in entry) ||
      !('errorMessage' in entry)
    ) {
      return;
    }

    const { propertyName, errorMessage } = entry as {
      propertyName?: string;
      errorMessage?: string;
    };

    if (
      propertyName !== ELIGIBILITY_REJECTION_CODE ||
      typeof errorMessage !== 'string'
    ) {
      return;
    }

    const normalized = errorMessage.trim().toLowerCase();

    if (normalized.startsWith(CARRIER_RULE_PREFIX)) {
      rejection.carrierFailures.push(errorMessage);
      return;
    }

    if (
      normalized.startsWith(SECURE_RATE_CON_RULE_PREFIX) &&
      rejection.secureRateConFailure === null
    ) {
      rejection.secureRateConFailure = errorMessage;
    }
  });

  return rejection;
};

type LoadSurePurchaseGate =
  | { kind: 'none' }
  | { kind: 'ineligible'; failures: string[] }
  | { kind: 'secureRateConRequired'; message: string }
  | { kind: 'acknowledgmentRequired' };

export const getLoadSurePurchaseGate = (
  commodityId: number | null,
  rejection: LoadSureRejection | null,
): LoadSurePurchaseGate => {
  const secureRateConFailure = rejection?.secureRateConFailure ?? null;

  if (secureRateConFailure !== null) {
    return { kind: 'secureRateConRequired', message: secureRateConFailure };
  }

  if (rejection !== null && rejection.carrierFailures.length > 0) {
    return { kind: 'ineligible', failures: rejection.carrierFailures };
  }

  if (!isTL() || !isRestrictedCommodity(commodityId)) {
    return { kind: 'none' };
  }

  if (hasAcceptedSecureRateCon()) {
    return { kind: 'none' };
  }

  if (isFeatureFlagEnabled(FeatureFlags.UseHighwaySecureRateCon)()) {
    return { kind: 'none' };
  }

  return { kind: 'acknowledgmentRequired' };
};
